/**
 * Whether a tenant must issue certified invoices on a given day (#177).
 *
 * The obligation is a dated parameter per jurisdiction and taxpayer
 * category, stored in the platform table `CertificationObligationPeriod`;
 * a tenant's category is dated too (`TenantTaxpayerCategoryPeriod`). A
 * tenant subject on the issue day issues CERTIFIED invoices through the
 * MCF; any other tenant issues ORDINARY invoices, which carry no security
 * element and no certification mention.
 *
 * A declared category that would exempt the tenant on a day (no obligation
 * period of its category that day) counts only once a platform operator has
 * validated it, and from its validation day on (#526); until then the
 * tenant is treated as if it had not declared it.
 *
 * A declared category period may also start a voluntary certification
 * (#528, `voluntaryCertification`): from the day it counts, the tenant is
 * subject whatever its category, and no later period ends it; a suspension
 * covering the tenant still applies, as to any subject tenant. It counts
 * like any declaration of a category with no obligation: once validated,
 * from the later of its effective day and its validation day.
 *
 * A suspension of the obligation (`CertificationObligationSuspension`,
 * #622) covering the tenant's counted category, or every category, makes
 * the tenant not subject from its `effectiveFrom` to the day before its
 * `effectiveUntil` (open-ended while null). While it covers the tenant,
 * every document is ORDINARY, credit notes of certified invoices included.
 *
 * Days are UTC calendar days: a period starts at 00:00 UTC on its
 * `effectiveFrom`. The registry admits only jurisdictions at UTC+0 all year.
 */
import { InvoiceError } from "../errors/index.js";
import { JURISDICTIONS } from "../jurisdictions/registry.js";
import { currencyDay } from "./certification-currency.js";
/** How an invoice is issued, frozen on `Invoice.issuanceRegime` at issue. */
export const ISSUANCE_REGIMES = {
    /** Signed by the jurisdiction's certification device (MCF/SECeF). */
    CERTIFIED: "CERTIFIED",
    /** Issued by the software alone: no device, no security element. */
    ORDINARY: "ORDINARY",
    /**
     * Sale of a subject tenant issued while its MCF is down under a declared,
     * notified outage (arrêté n°2025-0049, art. 6 g): no device, no security
     * element, outside the fiscal series, closed by a CERTIFIED regularisation.
     */
    PROVISIONAL: "PROVISIONAL",
};
/** True for a value of `ISSUANCE_REGIMES`. */
export function isIssuanceRegime(value) {
    return (value === ISSUANCE_REGIMES.CERTIFIED ||
        value === ISSUANCE_REGIMES.ORDINARY ||
        value === ISSUANCE_REGIMES.PROVISIONAL);
}
/**
 * Signing device that produced a certified invoice's security elements,
 * stored on `Invoice.fiscalModality`. Only MCF_CERTIFIE certifies the
 * document; NULL records no device (an ordinary invoice, or one signed
 * before the column existed).
 */
export const FISCAL_MODALITIES = {
    /** Physical MCF speaking the annex protocol. */
    MCF_CERTIFIE: "MCF_CERTIFIE",
    /** Virtual MCF (vMCF) signing with per-establishment keys. */
    MCF_VIRTUEL: "MCF_VIRTUEL",
    /** Simulator: an evaluation document, not a certified invoice. */
    SIMULATEUR: "SIMULATEUR",
    /** Device of an unknown kind, or outside the annex protocol — nothing certifies this document. */
    INCONNU: "INCONNU",
};
/**
 * Jurisdictions with a certification scheme, and the device that certifies
 * there: the registry entries whose `certifier` is set. A jurisdiction
 * absent from this list issues ordinary invoices only.
 */
export const CERTIFICATION_SCHEMES = Object.freeze(Object.fromEntries(Object.values(JURISDICTIONS).flatMap((profile) => profile.certifier ? [[profile.code, Object.freeze({ device: profile.certifier.device })]] : [])));
/** True when `jurisdiction` has a certification scheme. */
export function hasCertificationScheme(jurisdiction) {
    return Object.prototype.hasOwnProperty.call(CERTIFICATION_SCHEMES, jurisdiction);
}
/** Taxpayer categories an obligation period and a tenant declaration name. */
export const TAXPAYER_CATEGORIES = Object.freeze({
    DGE: { label: "Direction des grandes entreprises (DGE)" },
    DME: { label: "Directions des moyennes entreprises (DME)" },
    PE: { label: "Petite entreprise" },
    MICRO: { label: "Micro-entreprise" },
});
/** True for a code of `TAXPAYER_CATEGORIES`. */
export function isTaxpayerCategory(value) {
    return (typeof value === "string" && Object.prototype.hasOwnProperty.call(TAXPAYER_CATEGORIES, value));
}
const DGE_DME_REFERENCE = "CGI art. 564 (loi n°042-2024/ALT, art. 44) ; mise en obligation au 1er juillet 2026 des " +
    "grandes et moyennes entreprises (DG des impôts, leconomistedufaso.com, 06/04/2026)";
/**
 * The obligation periods seeded in every database by
 * `packages/api/prisma/sql/certification-obligation-guards.sql`: only the
 * step whose day is verified (docs/fiscal/2026-07-12-tva-facture-electronique.md
 * §2). A test keeps the two identical.
 */
export const CERTIFICATION_OBLIGATION_ORIGINS = Object.freeze([
    Object.freeze({
        id: "certification-obligation-bf-dge",
        jurisdiction: JURISDICTIONS["BF"].code,
        taxpayerCategory: "DGE",
        effectiveFrom: "2026-07-01",
        legalReference: DGE_DME_REFERENCE,
        createdById: "system",
    }),
    Object.freeze({
        id: "certification-obligation-bf-dme",
        jurisdiction: JURISDICTIONS["BF"].code,
        taxpayerCategory: "DME",
        effectiveFrom: "2026-07-01",
        legalReference: DGE_DME_REFERENCE,
        createdById: "system",
    }),
]);
/**
 * The category of the latest active period starting on or before the day
 * of `date`, or null. Two active periods starting the same day are refused.
 */
export function taxpayerCategoryAt(periods, date) {
    const day = currencyDay(date);
    let best = null;
    let tie = false;
    for (const period of periods) {
        if (period.withdrawnAt != null)
            continue;
        const start = currencyDay(period.effectiveFrom);
        if (start > day)
            continue;
        if (best === null || start > best.day) {
            best = { day: start, category: period.category };
            tie = false;
        }
        else if (start === best.day) {
            tie = true;
        }
    }
    if (tie && best !== null) {
        throw new InvoiceError(`Deux catégories de contribuable actives commencent le ${best.day}`, "PERIODE_CATEGORIE_AMBIGUE", 500);
    }
    return best?.category ?? null;
}
/** Prefix of `validatedById` on a period validated by the publisher's signed attestation (#603). */
export const PUBLISHER_VALIDATOR_PREFIX = "publisher:";
/**
 * Past this many days since the install's signed revocation list was
 * fetched or signed, the list is stale: a revocation it does not carry may
 * not have reached the install. An attested validation stands until a
 * revocation reaches it.
 */
export const ATTESTATION_REVOCATION_LIST_MAX_AGE_DAYS = 30;
/** From this many days without a successful fetch, the install warns. */
export const ATTESTATION_REVOCATION_LIST_WARNING_DAYS = 20;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Whether `period` was validated by the publisher's signed attestation. */
export function isAttestedValidation(period) {
    return period.validatedById?.startsWith(PUBLISHER_VALIDATOR_PREFIX) ?? false;
}
/**
 * Whether the install's revocation list is stale on `now`.
 * `revocationListAsOf` is the older of the list's last successful fetch and
 * its signed `issuedAt`, null when no list was ever fetched; stale when null
 * or more than ATTESTATION_REVOCATION_LIST_MAX_AGE_DAYS old. Attested
 * validations stand either way.
 */
export function revocationListStale(revocationListAsOf, now) {
    if (revocationListAsOf === null)
        return true;
    const fetched = new Date(revocationListAsOf).getTime();
    if (!Number.isFinite(fetched))
        return true;
    return now.getTime() - fetched > ATTESTATION_REVOCATION_LIST_MAX_AGE_DAYS * DAY_MS;
}
function hasObligationOn(input, category, day) {
    return input.obligationPeriods.some((period) => period.withdrawnAt == null &&
        period.jurisdiction === input.jurisdiction &&
        period.taxpayerCategory === category &&
        currencyDay(period.effectiveFrom) <= day);
}
/**
 * Whether a declaration of `category` needs a platform operator's
 * validation to count on the day of `date`: the jurisdiction has a
 * certification scheme and the category has no obligation period that day.
 */
export function categoryRequiresValidation(input, category, date) {
    return hasCertificationScheme(input.jurisdiction) && !hasObligationOn(input, category, currencyDay(date));
}
/**
 * Whether an active suspension of `input.jurisdiction` covers `category`
 * (null: a tenant with no counted category) on `day` (YYYY-MM-DD): its
 * category is null or equals `category`, `effectiveFrom <= day`, and its
 * `effectiveUntil` is null or later than `day`. A category-specific
 * suspension never covers a tenant with no counted category.
 */
export function obligationSuspendedOn(input, category, day) {
    return input.suspensions.some((suspension) => suspension.withdrawnAt == null &&
        suspension.jurisdiction === input.jurisdiction &&
        (suspension.taxpayerCategory === null || suspension.taxpayerCategory === category) &&
        currencyDay(suspension.effectiveFrom) <= day &&
        (suspension.effectiveUntil == null || day < currencyDay(suspension.effectiveUntil)));
}
/**
 * The tenant's counted category on `day`: a period whose category has no
 * obligation that day counts only when validated on or before that day.
 * Among the periods that count, the latest `effectiveFrom` wins, whatever
 * the validation days.
 */
function countedCategory(input, day) {
    return taxpayerCategoryAt(countingPeriods(input, day), day);
}
/** The category periods that count on `day`, whatever their effective day. */
function countingPeriods(input, day) {
    return input.categoryPeriods.filter((period) => hasObligationOn(input, period.category, day) ||
        (period.validatedAt != null && currencyDay(period.validatedAt) <= day));
}
/**
 * Whether a voluntary certification of the tenant (#528) is in force on the
 * day of `date`: an active period marked `voluntaryCertification` starts on
 * or before the day and counts that day. Later periods never end it. False
 * in a jurisdiction with no certification scheme.
 */
export function isVoluntarilyCertifying(input, date) {
    if (!hasCertificationScheme(input.jurisdiction))
        return false;
    const day = currencyDay(date);
    return countingPeriods(input, day).some((period) => period.voluntaryCertification === true &&
        period.withdrawnAt == null &&
        currencyDay(period.effectiveFrom) <= day);
}
/**
 * Whether a suspension of the obligation covers the tenant on the day of
 * `date`: one of its counted category, or one of every category. False in
 * a jurisdiction with no certification scheme, where nothing is suspended.
 */
export function isCertificationSuspended(input, date) {
    if (!hasCertificationScheme(input.jurisdiction))
        return false;
    const day = currencyDay(date);
    return obligationSuspendedOn(input, countedCategory(input, day), day);
}
/**
 * Whether the tenant must certify its invoices on the day of `date`:
 * its jurisdiction has a certification scheme, no suspension covers it
 * that day, and either no category counts that day (a tenant that
 * declared nothing stays subject), a voluntary certification is in force
 * (`isVoluntarilyCertifying`), or its category has an active obligation
 * period starting on or before the day.
 */
export function isSubjectToCertification(input, date) {
    if (!hasCertificationScheme(input.jurisdiction))
        return false;
    const day = currencyDay(date);
    const category = countedCategory(input, day);
    if (obligationSuspendedOn(input, category, day))
        return false;
    if (category === null)
        return true;
    if (isVoluntarilyCertifying(input, day))
        return true;
    return hasObligationOn(input, category, day);
}
/** The regime a tenant issues in on the day of `date`. */
export function issuanceRegimeAt(input, date) {
    return isSubjectToCertification(input, date)
        ? ISSUANCE_REGIMES.CERTIFIED
        : ISSUANCE_REGIMES.ORDINARY;
}
/**
 * Refuses ordinary issuance by a tenant subject to certification on the
 * issue day. There is no override; `hint` is appended to the message (the
 * opt-in to the transitional mode, when it is open to the tenant).
 */
export function assertOrdinaryIssuanceAllowed(subject, date, hint) {
    if (!subject)
        return;
    const day = date === undefined ? null : currencyDay(date);
    const when = day === null ? "à la date d'émission" : `le ${day.split("-").reverse().join("/")}`;
    throw new InvoiceError(`L'entreprise est soumise à la certification ${when} : elle ne peut émettre que des factures certifiées par le MCF.` +
        (hint ? ` ${hint}` : ""), "EMISSION_ORDINAIRE_INTERDITE", 409);
}
/**
 * The regime of a document. While a suspension of the obligation covers the
 * tenant (`suspended`), every document is ORDINARY, a credit note of a
 * certified invoice included (#622, D2). Otherwise a linked credit note of a
 * certified invoice is certified, and any other document, a linked credit
 * note of an ordinary invoice included, takes the regime of the issue day.
 */
export function documentIssuanceRegime(args) {
    if (args.suspended)
        return ISSUANCE_REGIMES.ORDINARY;
    if (args.originalRegime === ISSUANCE_REGIMES.CERTIFIED)
        return ISSUANCE_REGIMES.CERTIFIED;
    return args.regimeOfDay;
}
/**
 * The refusal of a certified credit note of nature other than RRR on an
 * ordinary invoice; null when the credit note is not one: the MCF
 * references an original by its MID and total counter, which an ordinary
 * invoice does not carry, and an RRR by the literal RRR.
 */
export function certifiedCreditNoteOnOrdinaryOriginalRefusal(args) {
    if (args.regime !== ISSUANCE_REGIMES.CERTIFIED)
        return null;
    if (args.originalRegime !== ISSUANCE_REGIMES.ORDINARY)
        return null;
    if (args.creditNoteType === "RRR")
        return null;
    return new InvoiceError(`La facture d'origine ${args.originalNumber} est une facture ordinaire, sans éléments de certification MCF : ` +
        `le MCF ne peut pas la référencer pour un avoir de nature ${args.creditNoteType ?? "non renseignée"}. ` +
        "Seul un avoir de nature RRR peut être certifié sur cette facture.", "AVOIR_ORIGINE_ORDINAIRE_NON_CERTIFIABLE", 400);
}
/** Throws `certifiedCreditNoteOnOrdinaryOriginalRefusal`, if any. */
export function assertCertifiedCreditNoteOnOrdinaryOriginal(args) {
    const refusal = certifiedCreditNoteOnOrdinaryOriginalRefusal(args);
    if (refusal !== null)
        throw refusal;
}
/**
 * `assertCertifiedCreditNoteOnOrdinaryOriginal` at draft creation, where the
 * regime of the day does not yet fix the regime of the issue: while a DGI
 * incident covers the instant (`dgiIncidentCovers`), the request that names
 * it may issue the draft ORDINARY, without the MCF (#622 A, `judgeContinuity`
 * step 4), so any nature is drafted. Its certified issue stays refused.
 */
export function assertCreditNoteDraftOnOrdinaryOriginal(args) {
    if (args.dgiIncidentCovers)
        return;
    assertCertifiedCreditNoteOnOrdinaryOriginal(args);
}
//# sourceMappingURL=certification-obligation.js.map